/**
 * ID Bridge Utility
 * 
 * PURPOSE: Transfer persistentId from embedded browsers (Instagram, Facebook, etc.) 
 * to native browsers (Safari, Chrome) when users switch.
 * 
 * WHY: Embedded browsers and native browsers have separate storage (cookies/localStorage).
 * Without a bridge, the same user appears as two different users in analytics.
 * 
 * HOW: Uses time-limited URL parameters to transfer the ID:
 * - Embedded browser: Add ?persid=player_abc&persts=timestamp to URL
 * - User switches browsers (URL is carried over)
 * - Native browser: Read persid from URL, save to storage, remove from URL
 * 
 * SECURITY: Bridge expires after 5 minutes to prevent identity hijacking from shared links
 */

import { cookie } from '@playcastdotio/Cookie';
import { createPlayerPersistentId } from '@playcastdotio/CreateId';

export const BRIDGE_EXPIRY_MS = 3 * 60 * 1000; // 3 minutes

/**
 * BRIDGE FLOW PART 1: Validation
 * 
 * Get valid bridge ID from URL if it exists and is not expired.
 * This is the first check when a page loads - did we come from an embedded browser?
 * 
 * Returns: 
 * - The player ID if valid bridge found (e.g., "player_abc123")
 * - null if no bridge, expired bridge, or invalid bridge
 */
export function getValidBridgeId(): string | null {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get('persid'); // persid = persistent ID
  const bridgeTs = searchParams.get('persts'); // persts = persistent timestamp
  
  // Must have BOTH parameters for a valid bridge
  // If only 'persid' exists (no timestamp), it's an old shared link - reject it
  if (!id || !bridgeTs) {
    if (id && !bridgeTs) {
      console.log('[ID Bridge] Found persid without persts - ignoring (likely shared link)');
    }
    return null;
  }
  
  // Check timestamp validity
  const timestamp = parseInt(bridgeTs, 10);
  if (isNaN(timestamp)) {
    console.log('[ID Bridge] Invalid persts - ignoring');
    return null;
  }
  
  const now = Date.now();
  const age = now - timestamp;
  
  // Check if within time window
  if (age < 0) {
    console.log('[ID Bridge] Bridge timestamp is in the future - ignoring');
    return null;
  }
  
  if (age > BRIDGE_EXPIRY_MS) {
    console.log(`[ID Bridge] Bridge expired (age: ${age}ms, max: ${BRIDGE_EXPIRY_MS}ms) - ignoring`);
    return null;
  }
  
  // Validate ID format (must start with 'player_')
  if (!id.startsWith('player_')) {
    console.log('[ID Bridge] Invalid ID format - ignoring');
    return null;
  }
  
  console.log(`[ID Bridge] Valid bridge found (age: ${age}ms):`, id);
  return id;
}

/**
 * BRIDGE FLOW PART 2: Storage Write
 * 
 * Set persistentId in cookie and localStorage.
 * This writes to the SAME storage location that getPersistentPlayerId() reads from.
 * 
 * Why both cookie and localStorage?
 * - Cookie: Works across subdomains, sent with requests
 * - localStorage: Persists longer, survives cookie clearing
 */
export function setPersistentPlayerId(id: string): void {
  const { set } = cookie();
  set('playcast-player-id', id);
  localStorage.setItem('playcast-player-id', id);
  console.log('[ID Bridge] Set persistentId:', id);
}

/**
 * BRIDGE FLOW PART 3: Storage Read
 * 
 * Get existing persistentId from cookie/localStorage.
 * Same logic as getPersistentPlayerId() but without creating new IDs.
 * 
 * Returns:
 * - Existing ID if found and valid
 * - null if no existing ID
 */
export function getExistingPersistentPlayerId(): string | null {
  const { get } = cookie();
  const id = get('playcast-player-id') || localStorage.getItem('playcast-player-id');
  
  // Validate format
  if (id && id.startsWith('player_')) {
    return id;
  }
  
  return null;
}

/**
 * BRIDGE FLOW PART 4: URL Management (Add)
 * 
 * Append persistentId to URL with timestamp.
 * This happens in EMBEDDED browsers to prepare the URL for when user switches.
 * 
 * Before: /q/shades?utm_source=instagram
 * After:  /q/shades?utm_source=instagram&persid=player_abc123&persts=1699123456789
 * 
 * When user clicks Instagram's "Open in Safari" button, this URL is carried over.
 */
export function appendPersistentIdToUrl(id: string): void {
  const url = new URL(window.location.href);
  const now = Date.now();
  
  url.searchParams.set('persid', id);
  url.searchParams.set('persts', now.toString());
  
  window.history.replaceState({}, '', url.toString());
  
  console.log('[ID Bridge] Added bridge params to URL:', { persid: id, persts: now });
}

/**
 * BRIDGE FLOW PART 5: URL Management (Remove)
 * 
 * Remove persistentId and bridge timestamp from URL.
 * This happens in NATIVE browsers after we've read and saved the ID.
 * 
 * Before: /q/shades?utm_source=instagram&persid=player_abc123&persts=1699123456789
 * After:  /q/shades?utm_source=instagram
 * 
 * Why remove? Cleaner URLs and prevents accidental sharing of bridge links.
 */
export function removePersistentIdFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;
  
  if (url.searchParams.has('persid')) {
    url.searchParams.delete('persid');
    changed = true;
  }
  
  if (url.searchParams.has('persts')) {
    url.searchParams.delete('persts');
    changed = true;
  }
  
  if (changed) {
    window.history.replaceState({}, '', url.toString());
    console.log('[ID Bridge] Removed bridge params from URL');
  }
}

/**
 * BRIDGE FLOW ORCHESTRATOR
 * 
 * Main function: Handle complete ID bridge setup.
 * Call this ONCE at the start of quickstart flow BEFORE any analytics.
 * 
 * SIMPLE FLOW:
 * 1. If URL has params → Use them, save to storage, clean URL
 * 2. If URL doesn't have params → Get/create ID, add to URL if mobile
 * 
 * @param isEmbeddedBrowser - Whether current browser is embedded (Instagram, Facebook, etc.)
 * @param isMobileDevice - Whether user is on a mobile device
 * @returns The persistentId to use for this session
 * 
 * EXAMPLES:
 * 
 * Scenario A - User clicks shared link with persid:
 *   URL: /q/shades?persid=player_abc&persts=123456789
 *   → Saves ID to storage
 *   → Cleans URL: /q/shades (always clean when params exist)
 *   Result: Clean URL, ID saved to storage ✓
 * 
 * Scenario B - User opens clean URL on mobile:
 *   URL: /q/shades (no params)
 *   → Gets/creates ID from storage
 *   → Adds to URL: /q/shades?persid=player_abc&persts=123456789
 *   Result: URL ready to copy/share ✓
 * 
 * Scenario C - User opens clean URL on desktop:
 *   URL: /q/shades (no params)
 *   → Gets/creates ID from storage
 *   → Keeps URL clean: /q/shades
 *   Result: No URL clutter ✓
 */
export function setupIdBridge(isEmbeddedBrowser: boolean, isMobileDevice = false): string {
  console.log('[ID Bridge] Setup started, isEmbedded:', isEmbeddedBrowser, 'isMobile:', isMobileDevice);
  
  // STEP 1: Check for valid bridge in URL
  // This catches users coming FROM embedded browsers or copying URLs between browsers
  const bridgeId = getValidBridgeId();
  
  if (bridgeId) {
    // URL HAS params - use them and clean them
    console.log('[ID Bridge] Using bridge ID from URL:', bridgeId);
    setPersistentPlayerId(bridgeId);
    removePersistentIdFromUrl();
    return bridgeId;
  }
  
  // URL DOESN'T HAVE params - get/create ID and add to URL if mobile
  
  // STEP 2: Check for existing ID in storage
  let existingId = getExistingPersistentPlayerId();
  
  // STEP 3: Create new ID if needed
  if (!existingId) {
    existingId = createPlayerPersistentId();
    setPersistentPlayerId(existingId);
    console.log('[ID Bridge] Created new persistentId:', existingId);
  } else {
    console.log('[ID Bridge] Using existing persistentId:', existingId);
  }
  
  // STEP 4: Add to URL if mobile
  if (isMobileDevice) {
    appendPersistentIdToUrl(existingId);
  }
  
  return existingId;
}

